import { useEffect, useRef } from "react"

import type { HelpArticleSuggestionActions } from "./help-article-suggestions"
import type { Editor } from "@tiptap/react"
import HelpArticleSuggestions from "./help-article-suggestions"

interface Props {
	editor: Editor | null
	anchorRef?: React.RefObject<HTMLElement | null>
	width?: number
}

const DEFAULT_POPOVER_WIDTH = 360

// Mounts the help-article popover and bridges the editor's "@" trigger to it.
// The Tiptap-renderer instance only dispatches "open-help-articles"; this is the
// instance that actually renders the popover and reacts to that event.
export function HelpArticlesBridge({ editor, anchorRef, width }: Props) {
	const suggestionsRef = useRef<HelpArticleSuggestionActions>(null)
	const caretRef = useRef<{ top: number; left: number } | null>(null)

	useEffect(() => {
		const editorDom = editor?.view.dom
		if (!editorDom) return

		const handleOpenHelpArticles = () => {
			if (editor) {
				const { from } = editor.state.selection
				const coords = editor.view.coordsAtPos(from)
				const container = anchorRef?.current?.getBoundingClientRect()
				caretRef.current = container
					? {
							top: coords.bottom - container.top,
							left: coords.left - container.left,
						}
					: null
			}
			suggestionsRef.current?.openHelpArticles()
		}

		editorDom.addEventListener("open-help-articles", handleOpenHelpArticles)
		return () => {
			editorDom.removeEventListener(
				"open-help-articles",
				handleOpenHelpArticles,
			)
		}
	}, [editor, anchorRef])

	const writeHelpArticle = ({
		title,
		linkUrl,
	}: {
		title: string
		linkUrl: string
	}) => {
		if (!editor) return
		const { from } = editor.state.selection
		const charBefore = editor.state.doc.textBetween(Math.max(0, from - 1), from)
		const chain = editor.chain()
		if (charBefore === "@") {
			chain.deleteRange({ from: from - 1, to: from })
		}
		const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${title}</a>&nbsp;`
		chain.insertContent(linkHtml).focus("end", { scrollIntoView: false }).run()
	}

	if (!editor) return null

	return (
		<HelpArticleSuggestions
			ref={suggestionsRef}
			editor={editor}
			command={writeHelpArticle}
			withoutPortal
			width={width ?? DEFAULT_POPOVER_WIDTH}
			initialOpen={false}
			anchorRef={anchorRef}
			caretRef={caretRef}
		/>
	)
}
